import { forwardRef } from 'react';
import { format } from 'date-fns';

const numberToWords = (amount) => {
  const ones = ["", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine",
    "Ten", "Eleven", "Twelve", "Thirteen", "Fourteen", "Fifteen", "Sixteen",
    "Seventeen", "Eighteen", "Nineteen"];
  const tens = ["", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"];

  const convertLessThanThousand = (n) => {
    if (n === 0) return "";
    if (n < 20) return ones[n];
    if (n < 100) return tens[Math.floor(n / 10)] + (n % 10 ? " " + ones[n % 10] : "");
    return ones[Math.floor(n / 100)] + " Hundred" + (n % 100 ? " " + convertLessThanThousand(n % 100) : "");
  };

  const convert = (n) => {
    if (n === 0) return "Zero";
    let result = "";
    if (n >= 1000000000) { result += convertLessThanThousand(Math.floor(n / 1000000000)) + " Billion "; n %= 1000000000; }
    if (n >= 1000000) { result += convertLessThanThousand(Math.floor(n / 1000000)) + " Million "; n %= 1000000; }
    if (n >= 1000) { result += convertLessThanThousand(Math.floor(n / 1000)) + " Thousand "; n %= 1000; }
    if (n > 0) result += convertLessThanThousand(n);
    return result.trim();
  };

  const dollars = Math.floor(amount);
  const cents = Math.round((amount - dollars) * 100);
  return `${convert(dollars)} and ${cents.toString().padStart(2, '0')}/100 Dollars`;
};

export const ACHPrintView = forwardRef(({ ach, llc }, ref) => {
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    try { return format(new Date(dateStr), 'MMMM dd, yyyy'); }
    catch { return 'Date'; }
  };

  if (!ach) return null;

  return (
    <div ref={ref} className="ach-document p-5" style={{ background: 'white', fontFamily: "'DM Sans', sans-serif", fontSize: '14px', width: '8.5in' }}>
      {/* Header */}
      <div className="border-b-2 border-slate-900 pb-1 mb-4">
        <h1 className="font-bank text-xl font-bold text-slate-900 mb-0">ACH TRANSFER INSTRUCTIONS</h1>
        <div className="flex justify-between items-end">
          <p className="text-sm text-slate-500">Date: {formatDate(ach.effective_date || ach.created_at)}</p>
          <span className="text-xs font-mono bg-slate-100 px-2 py-0.5 rounded text-slate-600">SEC: {ach.sec_code}</span>
        </div>
      </div>

      {/* Originator */}
      <div className="mb-4">
        <h2 className="text-sm font-bold text-slate-700 uppercase tracking-wider mb-1">Originator</h2>
        <div className="bg-slate-50 p-3 rounded border border-slate-200">
          <div className="font-semibold text-base text-slate-900">{ach.originator_name || llc?.company_name || '-'}</div>
          {llc && (
            <div className="text-slate-600 text-sm leading-tight">
              {llc.address_line1}{llc.address_line2 && `, ${llc.address_line2}`}, {llc.city}, {llc.state} {llc.zip_code}
            </div>
          )}
          <div className="grid grid-cols-2 gap-2 mt-2 text-sm">
            <div><span className="text-slate-500">Routing:</span> <span className="font-mono">{ach.originator_routing || '-'}</span></div>
            <div><span className="text-slate-500">Account:</span> <span className="font-mono">{ach.originator_account || '-'}</span></div>
          </div>
        </div>
      </div>

      {/* Beneficiary */}
      <div className="mb-4">
        <h2 className="text-sm font-bold text-slate-700 uppercase tracking-wider mb-1">Beneficiary (Receiver)</h2>
        <div className="wire-field"><span className="wire-field-label">Name:</span><span className="wire-field-value">{ach.beneficiary_name}</span></div>
        {ach.beneficiary_address && (
          <div className="wire-field"><span className="wire-field-label">Address:</span><span className="wire-field-value">{ach.beneficiary_address}</span></div>
        )}
        {ach.beneficiary_city_state_zip && (
          <div className="wire-field"><span className="wire-field-label">City, State, ZIP:</span><span className="wire-field-value">{ach.beneficiary_city_state_zip}</span></div>
        )}
      </div>

      {/* Beneficiary Bank */}
      <div className="mb-4">
        <h2 className="text-sm font-bold text-slate-700 uppercase tracking-wider mb-1">Beneficiary Bank</h2>
        {ach.beneficiary_bank_name && (
          <div className="wire-field"><span className="wire-field-label">Bank Name:</span><span className="wire-field-value">{ach.beneficiary_bank_name}</span></div>
        )}
        <div className="wire-field"><span className="wire-field-label">Routing Number:</span><span className="wire-field-value font-mono">{ach.beneficiary_routing}</span></div>
        <div className="wire-field"><span className="wire-field-label">Account Number:</span><span className="wire-field-value font-mono">{ach.beneficiary_account}</span></div>
        <div className="wire-field"><span className="wire-field-label">Account Type:</span><span className="wire-field-value">{(ach.beneficiary_account_type || 'checking').charAt(0).toUpperCase() + (ach.beneficiary_account_type || 'checking').slice(1)}</span></div>
      </div>

      {/* Transfer Details */}
      <div className="mb-4">
        <h2 className="text-sm font-bold text-slate-700 uppercase tracking-wider mb-1">Transfer Details</h2>
        <div className="wire-field">
          <span className="wire-field-label">Amount:</span>
          <span className="wire-field-value text-base font-bold">
            {formatCurrency(ach.amount)} ({numberToWords(ach.amount || 0)})
          </span>
        </div>
        <div className="wire-field"><span className="wire-field-label">SEC Code:</span><span className="wire-field-value font-mono">{ach.sec_code}</span></div>
        <div className="wire-field"><span className="wire-field-label">Effective Date:</span><span className="wire-field-value">{formatDate(ach.effective_date)}</span></div>
        {ach.purpose && (
          <div className="wire-field"><span className="wire-field-label">Purpose:</span><span className="wire-field-value">{ach.purpose}</span></div>
        )}
        {ach.reference && (
          <div className="wire-field"><span className="wire-field-label">Reference:</span><span className="wire-field-value">{ach.reference}</span></div>
        )}
        {ach.addenda && (
          <div className="wire-field"><span className="wire-field-label">Addenda:</span><span className="wire-field-value">{ach.addenda}</span></div>
        )}
      </div>

      {/* Authorization */}
      <div className="mt-5 pt-4 border-t border-slate-300">
        <div className="grid grid-cols-2 gap-6">
          <div>
            <div className="border-b border-slate-400 h-8 mb-1"></div>
            <span className="text-[10px] text-slate-500 uppercase tracking-wide">Authorized Signature</span>
          </div>
          <div>
            <div className="border-b border-slate-400 h-8 mb-1"></div>
            <span className="text-[10px] text-slate-500 uppercase tracking-wide">Date</span>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="mt-3 pt-2 border-t border-slate-200 text-center">
        <p className="text-[10px] text-slate-400">
          This document contains confidential ACH transfer instructions. Please handle accordingly.
        </p>
      </div>

      <style>{`
        @media print {
          .ach-document {
            width: 8.5in !important;
            padding: 0.4in 0.6in !important;
            margin: 0 !important;
          }
        }
      `}</style>
    </div>
  );
});

ACHPrintView.displayName = 'ACHPrintView';
